import ISalesProductPrice from "@/interfaces/sales-product-price.interfaces";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";
import {
  getAllSalesProductPrices,
  type GetAllSalesProductPricesResponse,
  type SalesProductPrice as ApiSalesProductPrice,
} from "@/api/sales-product-price/get-all-sales-product-prices";
import { getSalesProductPriceById } from "@/api/sales-product-price/get-sales-product-price-by-id";

class SalesProductPriceService {
  async getAllSalesProductPrice(
    filter: IFilter<ISalesProductPrice>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<ISalesProductPrice>> {
    try {
      const apiResponse: GetAllSalesProductPricesResponse = await getAllSalesProductPrices(
        pagination,
        filter as unknown as IFilter<ApiSalesProductPrice>[]
      );

      type MaybeSalesProductPrice = ApiSalesProductPrice & {
        planName?: string;
        planDescription?: string;
        planContrast?: boolean;
        fixedMonthlyFee?: number;
        startDate?: string;
        endDate?: string | null;
        rowDate?: string;
        pricePerEvent?: number;
        eventsIncludedMonthlyFee?: number;
        feePerSalesConvertion?: number;
        fixedFeePerSalesConvertion?: number;
      };

      const mappedPlans: ISalesProductPrice[] = (apiResponse.items || []).map(
        (raw: ApiSalesProductPrice) => {
          const item = raw as MaybeSalesProductPrice;

          const resolvedPlanName = item.planName ?? item.salesProduct?.name ?? "";
          const resolvedDescription = item.planDescription ?? item.salesProduct?.description ?? "";
          const resolvedStart = item.startDate ?? item.validFrom;
          const resolvedEnd = item.endDate ?? item.validTo ?? null;
          const resolvedFixedMonthly =
            item.fixedMonthlyFee ?? (typeof item.price === "number" ? item.price : 0);
          const resolvedRowDate = item.rowDate ?? item.createdAt;

          return {
            id: item.id,
            planName: resolvedPlanName,
            salesProductId: item.salesProductId,
            startDate: resolvedStart ? new Date(resolvedStart) : new Date(),
            endDate: resolvedEnd ? new Date(resolvedEnd) : null,
            pricePerEvent: item.pricePerEvent ?? 0,
            eventsIncludedMonthlyFee: item.eventsIncludedMonthlyFee ?? 0,
            feePerSalesConvertion: item.feePerSalesConvertion ?? 0,
            fixedFeePerSalesConvertion: item.fixedFeePerSalesConvertion ?? 0,
            fixedMonthlyFee: resolvedFixedMonthly,
            planDescription: resolvedDescription,
            planContrast: item.planContrast ?? false,
            rowDate: resolvedRowDate ? new Date(resolvedRowDate) : new Date(),
          } as ISalesProductPrice;
        }
      );

      const result = {
        items: mappedPlans,
        totalCount: apiResponse.totalCount,
        pageNumber: apiResponse.pageNumber,
        pageSize: apiResponse.pageSize,
        hasNextPage: apiResponse.hasNextPage,
        hasPreviousPage: apiResponse.hasPreviousPage,
      };

      return result;
    } catch (error) {
      console.error("❌ [SERVICE] Erro ao buscar preços de produtos de venda:", error);
      throw error;
    }
  }

  async getById(salesProductPriceId: number): Promise<ISalesProductPrice> {
    try {
      type ApiItemWithOptionalPlanFields = ApiSalesProductPrice & {
        planName?: string;
        planDescription?: string;
        planContrast?: boolean;
        fixedMonthlyFee?: number;
        startDate?: string;
        endDate?: string | null;
        rowDate?: string;
        pricePerEvent?: number;
        eventsIncludedMonthlyFee?: number;
        feePerSalesConvertion?: number;
        fixedFeePerSalesConvertion?: number;
      };

      const raw = (await getSalesProductPriceById(
        salesProductPriceId
      )) as ApiItemWithOptionalPlanFields;

      const resolvedPlanName = raw.planName ?? raw.salesProduct?.name ?? "";
      const resolvedDescription = raw.planDescription ?? raw.salesProduct?.description ?? "";
      const resolvedStart = raw.startDate ?? raw.validFrom;
      const resolvedEnd = raw.endDate ?? raw.validTo ?? null;
      const resolvedFixedMonthly =
        raw.fixedMonthlyFee ?? (typeof raw.price === "number" ? raw.price : 0);
      const resolvedRowDate = raw.rowDate ?? raw.createdAt;

      return {
        id: raw.id,
        planName: resolvedPlanName,
        salesProductId: raw.salesProductId,
        startDate: resolvedStart ? new Date(resolvedStart) : new Date(),
        endDate: resolvedEnd ? new Date(resolvedEnd) : null,
        pricePerEvent: raw.pricePerEvent ?? 0,
        eventsIncludedMonthlyFee: raw.eventsIncludedMonthlyFee ?? 0,
        feePerSalesConvertion: raw.feePerSalesConvertion ?? 0,
        fixedFeePerSalesConvertion: raw.fixedFeePerSalesConvertion ?? 0,
        fixedMonthlyFee: resolvedFixedMonthly,
        planDescription: resolvedDescription,
        planContrast: raw.planContrast ?? false,
        rowDate: resolvedRowDate ? new Date(resolvedRowDate) : new Date(),
      } as ISalesProductPrice;
    } catch (error) {
      console.error("❌ [SERVICE] Erro ao buscar plano por ID:", error);
      throw error;
    }
  }
}

const salesProductPriceService = new SalesProductPriceService();
export default salesProductPriceService;
